// "Add visits to my calendar": an .ics file with the upcoming visits, ticket visits and calibration dates,
// as all-day events, for Google Calendar, Outlook or a phone.
import dayjs from 'dayjs';

const icsText = (s) => String(s ?? '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
const icsDate = (ymd) => ymd.replace(/-/g, '');

// events: [{ uid, start, end, title, description }] with 'YYYY-MM-DD' dates (end inclusive)
export const buildIcs = (calendarName, events) => {
    const stamp = dayjs().format('YYYYMMDD[T]HHmmss');
    const lines = [
        'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Greenvironment//Project visits//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
        `X-WR-CALNAME:${icsText(calendarName)}`,
        ...events.flatMap(e => [
            'BEGIN:VEVENT',
            `UID:${e.uid}@greenvironment`,
            `DTSTAMP:${stamp}`,
            `DTSTART;VALUE=DATE:${icsDate(e.start)}`,
            `DTEND;VALUE=DATE:${icsDate(dayjs(e.end || e.start).add(1, 'day').format('YYYY-MM-DD'))}`,
            `SUMMARY:${icsText(e.title)}`,
            ...(e.description ? [`DESCRIPTION:${icsText(e.description)}`] : []),
            'END:VEVENT',
        ]),
        'END:VCALENDAR',
    ];
    return lines.join('\r\n');
};

export const downloadIcs = (fileName, ics) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    a.download = fileName.replace(/[^\w.-]+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

// Upcoming (today onwards) sub-task visits, scheduled ticket visits and calibration dates
export const upcomingEvents = ({ facilityName, subTasks = [], tickets = [], calibrations = [] }) => {
    const today = dayjs().format('YYYY-MM-DD');
    return [
        ...subTasks.filter(t => t.end_date >= today && !['Completed', 'Cancelled'].includes(t.status)).map(t => ({
            uid: `subtask-${t.id}`, start: t.start_date, end: t.end_date,
            title: `${t.sub_task_name} · ${facilityName}`,
            description: [t.task_name, t.spoc?.name ? `Engineer: ${t.spoc.name}` : 'Engineer to be assigned'].filter(Boolean).join('\n'),
        })),
        ...tickets.filter(t => t.scheduled_date && t.scheduled_date >= today && t.stage !== 'resolved').map(t => ({
            uid: `ticket-${t.id}`, start: t.scheduled_date, end: t.scheduled_date,
            title: `Ticket visit ${t.id} · ${facilityName}`,
            description: [t.title, t.engineer?.name ? `Engineer: ${t.engineer.name}` : null].filter(Boolean).join('\n'),
        })),
        ...calibrations.filter(c => c.date >= today && c.status !== 'Completed').map(c => ({
            uid: `calibration-${c.id}`, start: c.date, end: c.date, title: `Calibration visit · ${facilityName}`,
        })),
    ];
};
